/**
 * WelcomeScreen — Empty state for new conversations
 *
 * Demo-port design (R2): 6 starter prompt cards with industrial defaults.
 * Clicking a card fires `onSelectPrompt(prompt)` which routes through the
 * real `useChatStream` pipeline (NOT a mock).
 *
 * The starter prompts are an inline constant — they're presentation copy,
 * not catalog data. When a tenant wants custom starter prompts, that
 * lands as a separate "tenant starter prompts" config row.
 */

import {
  Wrench, BarChart3, ClipboardList,
  FileText, ShieldCheck, Clock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ArgusEye } from '@/components/icons';

interface StarterPrompt {
  icon: LucideIcon;
  title: string;
  prompt: string;
}

const STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: Wrench,
    title: 'Diagnose equipment issue',
    prompt: 'I have a piece of equipment that is making an unusual noise. What should I check first?',
  },
  {
    icon: BarChart3,
    title: 'Summarize recent trends',
    prompt: 'Give me a high-level summary of the most important trends from the last 7 days.',
  },
  {
    icon: ClipboardList,
    title: 'Generate a checklist',
    prompt: 'Generate a pre-shift safety inspection checklist for a typical operating environment.',
  },
  {
    icon: FileText,
    title: 'Look up an SOP',
    prompt: 'Find the relevant SOP for lock-out / tag-out on a high-voltage breaker.',
  },
  {
    icon: ShieldCheck,
    title: 'Check compliance status',
    prompt: 'What is our current compliance status across active certifications, and which items expire within 30 days?',
  },
  {
    icon: Clock,
    title: 'Draft a shift handover',
    prompt: 'Draft a shift-handover report for the last 12 hours covering alarms, work completed, and open issues.',
  },
];

interface WelcomeScreenProps {
  onSelectPrompt: (prompt: string) => void;
}

export function AskArgusWelcome({ onSelectPrompt }: WelcomeScreenProps) {
  return (
    <div
      className="flex-1 flex flex-col items-center justify-center px-8 py-12"
      data-testid="ask-argus-welcome"
    >
      <div className="max-w-2xl w-full text-center space-y-8">
        {/* Logo + greeting */}
        <div className="space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <ArgusEye state="idle" className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">How can I help you today?</h1>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Ask me anything about your operations, equipment, maintenance, safety procedures, or compliance requirements.
          </p>
        </div>

        {/* Starter prompts */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {STARTER_PROMPTS.map((prompt, i) => {
            const Icon = prompt.icon;
            return (
              <button
                key={i}
                data-testid="welcome-starter-prompt"
                onClick={() => onSelectPrompt(prompt.prompt)}
                className="flex items-start gap-3 p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:bg-primary/5 transition-all text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
                  <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div>
                  <p className="text-sm font-medium group-hover:text-primary transition-colors">
                    {prompt.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {prompt.prompt}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Keyboard shortcuts hint */}
        <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground/50">
          <span><kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">/</kbd> commands</span>
          <span><kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">@</kbd> mention agent</span>
          <span><kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">Enter</kbd> send</span>
        </div>
      </div>
    </div>
  );
}
